import { Suspense, useEffect, useRef, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { PageTransition } from "@/components/mobile/PageTransition";
import { AnimatedRoutes } from "@/components/native/AnimatedRoutes";
import { useSwipeBack } from "@/hooks/useSwipeBack";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageSkeleton } from "@/components/PageSkeleton";
import { UserProfileProvider } from "@/contexts/UserProfileContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { useProfileSafe } from "@/contexts/UserProfileContext";
import { useSessionIntegrity } from '@/hooks/useSessionIntegrity';
import { useNoStoreMeta } from '@/hooks/useNoStoreMeta';
import { useAuthSafe } from '@/contexts/AuthContext';
import { WidgetEventProvider } from "@/hooks/useWidgetEventBus";
import NotFound from "./pages/NotFound";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import ScrollToTop from "@/components/ScrollToTop";
import { RouteProgress } from "@/components/RouteProgress";
import { ConnectionStatus } from "@/components/ConnectionStatus";
import { RouteTransitionGuard } from "@/components/RouteTransitionGuard";
import { AppShellFallback } from "@/components/AppShellFallback";

// React Query Devtools — only on true localhost (not staging/preview)
import { isDevelopment } from "@/utils/environment";
const ReactQueryDevtools = (import.meta.env.DEV && isDevelopment())
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      }))
    )
  : null;

// Lazy-load non-critical UI components
const ChatAssistant = lazy(() => import("@/components/ChatAssistant").then(m => ({ default: m.ChatAssistant })));
const NetworkStatus = lazy(() => import("@/components/mobile/NetworkStatus").then(m => ({ default: m.NetworkStatus })));
const AnnouncementBanner = lazy(() => import("@/components/announcements/AnnouncementBanner").then(m => ({ default: m.AnnouncementBanner })));
const GlobalFeedbackWidget = lazy(() => import("@/components/beta/GlobalFeedbackWidget"));
const TriggeredFeedbackModal = lazy(() => import("@/components/beta/TriggeredFeedbackModal").then(m => ({ default: m.TriggeredFeedbackModal })));
const BetaExpiryBanner = lazy(() => import("@/components/beta/BetaExpiryBanner").then(m => ({ default: m.BetaExpiryBanner })));
const BetaCampaignActivator = lazy(() => import("@/components/beta/BetaCampaignActivator").then(m => ({ default: m.BetaCampaignActivator })));
const CookieConsentBanner = lazy(() => import("@/components/CookieConsentBanner").then(m => ({ default: m.CookieConsentBanner })));
// DiagnosticsPanel: dev-only, gated to localhost so users never see the floating wrench
const DiagnosticsPanel = (import.meta.env.DEV && isDevelopment())
  ? lazy(() => import("@/components/diagnostics/DiagnosticsPanel").then(m => ({ default: m.DiagnosticsPanel })))
  : null;

// Route modules
import { publicRoutes } from "@/routes/publicRoutes";
import { studioRoutes } from "@/routes/studioRoutes";
import { authRoutes } from "@/routes/authRoutes";
import { adminRoutes } from "@/routes/adminRoutes";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Expose so destroySession() can wipe all cached queries on logout
// (prevents PII from leaking into the next session inside a shared browser).
if (typeof window !== 'undefined') {
  (window as any).__sirppiQueryClient = queryClient;
}

const AppContent = () => {
  // Session integrity check runs inside its own useEffect, so it never blocks
  // first paint — but keep it gated to avoid extra work during cold start.
  useSessionIntegrity({ checkOnMount: true, autoClear: true, showNotification: true });

  const auth = useAuthSafe();
  // Inject Cache-Control: no-store while authenticated so the back-button
  // after logout cannot resurrect a previous user's screens from bfcache.
  useNoStoreMeta(!!auth?.isAuthenticated);

  const { profile } = useProfileSafe();

  // Defer prefetching - only start after initial render is complete
  useEffect(() => {
    if (!profile?.id) return;
    const timer = setTimeout(() => {
      import('@/hooks/useSmartPrefetcher'); // Dynamic import - don't block render
    }, 5000);
    return () => clearTimeout(timer);
  }, [profile?.id]);

  // iOS-style edge-swipe back navigation (no-op on Android/desktop).
  useSwipeBack();

  // Use the branded AppShellFallback for the very first Suspense resolve,
  // then switch to the slim PageSkeleton bar for subsequent navigations
  // (which preserves the previous page underneath).
  const firstPaintDoneRef = useRef(false);
  const SuspenseFallback = firstPaintDoneRef.current ? <PageSkeleton /> : <AppShellFallback />;
  useEffect(() => {
    firstPaintDoneRef.current = true;
  }, []);

  return (
    <>
      <PageTransition>
        <Suspense fallback={SuspenseFallback}>
          <AnimatedRoutes>
            <Routes>
              {publicRoutes}
              {studioRoutes}
              {authRoutes}
              {adminRoutes}
              <Route
                path="*"
                element={
                  <RouteTransitionGuard fallback={<AppShellFallback />}>
                    <NotFound />
                  </RouteTransitionGuard>
                }
              />
            </Routes>
          </AnimatedRoutes>
        </Suspense>
      </PageTransition>
      {/* Non-critical overlays loaded after main content */}
      <Suspense fallback={null}>
        <NetworkStatus />
        <AnnouncementBanner />
        <BetaExpiryBanner />
        <BetaCampaignActivator />
        <ChatAssistant />
        <GlobalFeedbackWidget />
        <TriggeredFeedbackModal />
        <CookieConsentBanner />
        {DiagnosticsPanel && <DiagnosticsPanel />}
      </Suspense>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <UserProfileProvider>
              <WidgetEventProvider>
                <TooltipProvider>
                  <ScrollToTop />
                  <RouteProgress />
                  <ConnectionStatus />
                  <PaymentTestModeBanner />
                  <Toaster />
                  <Sonner />
                  <AppContent />
                  {/* React Query Devtools — dev only, zero production cost */}
                  {ReactQueryDevtools && (
                    <Suspense fallback={null}>
                      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
                    </Suspense>
                  )}
                </TooltipProvider>
              </WidgetEventProvider>
            </UserProfileProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
